import {useSelector} from 'react-redux';
import type {RootState} from '@/store';
import {aiPromptBoxActions} from '@Components/ai-prompt-box/ai-prompt-box.reducer';
import type {AiPromptBox, SelectedContextualMenuItems} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {useAppDispatch} from '@/hooks';
import type {ContextualMenuGroup} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {getDefaultAiPromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box-library';
import {useCallback, useMemo, useRef} from 'react';
import {useLatest} from 'ahooks';
import type {DeepPartial} from '@/global';
import type {LoadingAttachment, PromptAttachment, UserImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {createLoadingAttachments} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {isPromptBoxProcessing} from '@Components/ai-prompt-box/ai-prompt-box-library';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';

export interface AiPromptBoxActions {
  update: (options: DeepPartial<AiPromptBox>) => void;
  setActiveIfNotProcessing: VoidFunction;
  addAttachments: (attachments: PromptAttachment[], loadingAttachments?: LoadingAttachment[]) => void;
  addLoadingAttachments: (count: number) => string[];
  removeLoadingAttachments: (loadingIds: string[]) => void;
  removeImageAttachments: (hashedFilenames: string[]) => void;
  addPendingImageAttachments: (attachments: UserImageAttachment[]) => void;
  resolvePendingAttachment: (localId: string, resolved: UserImageAttachment) => void;
  markPendingAttachmentFailed: (localId: string) => void;
  markPendingAttachmentUploading: (localId: string) => void;
  removePendingAttachment: (localId: string) => void;
}

export interface UseAiPromptBox {
  state: AiPromptBox;
  actions: AiPromptBoxActions;
  selectedContextualMenuItems: SelectedContextualMenuItems;
  isInitialized: boolean;
}

const getSelectedContextualMenuItems = (groups?: ContextualMenuGroup[]): SelectedContextualMenuItems => {
  if (!groups) {
    return {};
  }

  const result: SelectedContextualMenuItems = {};

  groups.forEach((group) => {
    const selectedItems = group.items.filter((item) => item.active).map((item) => item.value);

    if (selectedItems.length > 0) {
      result[group.key] = selectedItems;
    }
  });

  return result;
};

export const useAiPromptBox = (id: string, initialConfig?: Partial<AiPromptBox>, isMobile = false): UseAiPromptBox => {
  const aiPromptBoxReduxState = useSelector((state: RootState) => state.aiPromptBox);
  const aiPromptBoxState = aiPromptBoxReduxState[id] ?? undefined;
  const dispatch = useAppDispatch();
  const initializedIds = useRef<Set<string>>(new Set());

  const update = useCallback(
    (options: DeepPartial<AiPromptBox>): void => {
      dispatch(
        aiPromptBoxActions.updateAiPromptBox({
          id,
          value: options,
        })
      );
    },
    [dispatch, id]
  );

  const addAttachments = useCallback(
    (attachments: PromptAttachment[], loadingAttachments?: LoadingAttachment[]) => {
      dispatch(
        aiPromptBoxActions.addAttachments({
          id,
          attachments,
          loadingAttachments,
        })
      );
    },
    [dispatch, id]
  );

  const addLoadingAttachments = useCallback(
    (count: number): string[] => {
      const {loadingIds, newLoadingAttachments} = createLoadingAttachments(count);
      dispatch(
        aiPromptBoxActions.addLoadingAttachments({
          id,
          loadingAttachments: newLoadingAttachments,
        })
      );
      return loadingIds;
    },
    [dispatch, id]
  );

  const removeLoadingAttachments = useCallback(
    (loadingIds: string[]): void => {
      dispatch(
        aiPromptBoxActions.removeLoadingAttachments({
          id,
          loadingIds,
        })
      );
    },
    [dispatch, id]
  );

  const removeImageAttachments = useCallback(
    (hashedFilenames: string[]): void => {
      dispatch(
        aiPromptBoxActions.removeImageAttachments({
          id,
          hashedFilenames,
        })
      );
    },
    [dispatch, id]
  );

  const addPendingImageAttachments = useCallback(
    (attachments: UserImageAttachment[]): void => {
      dispatch(
        aiPromptBoxActions.addPendingImageAttachments({
          id,
          attachments,
        })
      );
    },
    [dispatch, id]
  );

  const resolvePendingAttachment = useCallback(
    (localId: string, resolved: UserImageAttachment): void => {
      dispatch(
        aiPromptBoxActions.resolvePendingAttachment({
          id,
          localId,
          resolved,
        })
      );
    },
    [dispatch, id]
  );

  const markPendingAttachmentFailed = useCallback(
    (localId: string): void => {
      dispatch(
        aiPromptBoxActions.markPendingAttachmentFailed({
          id,
          localId,
        })
      );
    },
    [dispatch, id]
  );

  const markPendingAttachmentUploading = useCallback(
    (localId: string): void => {
      dispatch(
        aiPromptBoxActions.markPendingAttachmentUploading({
          id,
          localId,
        })
      );
    },
    [dispatch, id]
  );

  const removePendingAttachment = useCallback(
    (localId: string): void => {
      dispatch(
        aiPromptBoxActions.removePendingAttachment({
          id,
          localId,
        })
      );
    },
    [dispatch, id]
  );

  const promptBoxStateRef = useLatest(aiPromptBoxState?.promptBoxState);

  const setActiveIfNotProcessing = (): void => {
    if (!isPromptBoxProcessing(promptBoxStateRef.current ?? PromptBoxState.IDLE)) {
      update({promptBoxState: PromptBoxState.ACTIVE});
    }
  };

  const actions = useMemo(
    () => ({
      update,
      setActiveIfNotProcessing,
      addAttachments,
      addLoadingAttachments,
      removeLoadingAttachments,
      removeImageAttachments,
      addPendingImageAttachments,
      resolvePendingAttachment,
      markPendingAttachmentFailed,
      markPendingAttachmentUploading,
      removePendingAttachment,
    }),
    [update, addAttachments, addLoadingAttachments, removeLoadingAttachments, removeImageAttachments, addPendingImageAttachments, resolvePendingAttachment, markPendingAttachmentFailed, markPendingAttachmentUploading, removePendingAttachment]
  );

  if (!aiPromptBoxState && !initializedIds.current.has(id) && initialConfig !== undefined) {
    const defaultState = getDefaultAiPromptBoxState(isMobile);
    dispatch(
      aiPromptBoxActions.initializeAiPromptBox({
        id,
        value: {
          ...defaultState,
          ...initialConfig,
          transcription: {
            ...defaultState.transcription,
            ...initialConfig.transcription,
          },
        },
      })
    );
    initializedIds.current.add(id);
  }

  const defaultState = getDefaultAiPromptBoxState(isMobile);
  const currentState = aiPromptBoxState ?? {
    ...defaultState,
    ...(initialConfig ?? {}),
    transcription: {
      ...defaultState.transcription,
      ...(initialConfig?.transcription ?? {}),
    },
  };

  const selectedContextualMenuItems = useMemo(() => getSelectedContextualMenuItems(currentState.contextualMenu), [currentState.contextualMenu]);

  return {
    state: currentState,
    actions,
    selectedContextualMenuItems,
    isInitialized: !!aiPromptBoxState,
  };
};
